import { FormControl, InputLabel, MenuItem } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect } from 'react';
import { WO2Select } from 'src/common';
import { CashAccountSecurity } from '../store/types';

export const defaultCurrencyCode = 'AUD';

interface Props {
  securities: CashAccountSecurity[];
  selectedSecurityId: number | null;
  setSecurityParameter: (securityId: number) => void;
}

const tradeDateMillis = (security: CashAccountSecurity): number => {
  const tradeDate = DateTime.fromISO(security.mostRecentTradeDate ?? '');
  return tradeDate.isValid ? tradeDate.toMillis() : 0;
};

export const selectDefaultSecurity = (securities: CashAccountSecurity[]): CashAccountSecurity | null => {
  if (securities.length === 0) {
    return null;
  }

  return (
    securities.find((security) => security.currencyCode === defaultCurrencyCode) ??
    [...securities].sort((a, b) => tradeDateMillis(b) - tradeDateMillis(a))[0]
  );
};

export const CurrencySelector = ({ securities, selectedSecurityId, setSecurityParameter }: Props): JSX.Element => {
  const selectedSecurity = securities.find((security) => security.securityId === selectedSecurityId);

  useEffect(() => {
    if (securities.some((security) => security.securityId === selectedSecurityId)) {
      return;
    }

    const defaultSecurity = selectDefaultSecurity(securities);
    if (defaultSecurity) {
      setSecurityParameter(defaultSecurity.securityId);
    }
  }, [securities, selectedSecurityId, setSecurityParameter]);

  return (
    <FormControl variant="outlined">
      <InputLabel id="currency-select-label">Currency</InputLabel>
      <WO2Select
        labelId="currency-select-label"
        id="currency-select"
        label="Currency"
        value={selectedSecurity?.securityId ?? ''}
        onChange={(event) => setSecurityParameter(Number(event.target.value))}
      >
        {securities.map((security) => (
          <MenuItem key={security.securityId} value={security.securityId}>
            {security.currencyCode}
          </MenuItem>
        ))}
      </WO2Select>
    </FormControl>
  );
};
